import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../styles/LandingPageLifts.module.scss';

// Context.
import { OmaLummeApp, EvChargingApp } from '../../../omaLummeContext';

// Components.
import Liftup from '../../Common/Liftup';

// Actions.
import { fetchEvChargingStatus } from '../../../actions';
// Types.
import { EmablerChargerUI, EvChargingStatus } from '../../../types/evCharging';
// Utils.
import { contractIsEvCharging } from '../../../utils/evCharging';

const EVChargerLift: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { config, basicData, users, selectedMeteringPointContract } = OmaLummeApp.useState();
  const { evChargerInfo, chargerStatus } = EvChargingApp.useState();

  const dispatchEV = EvChargingApp.useDispatch();

  const countAvailableSockets = (data: EmablerChargerUI[]) =>
    data.reduce((total, charger) => {
      return (
        total +
        charger.sockets.reduce((count, socket) => {
          return count + (socket.status === 'Available' ? 1 : 0);
        }, 0)
      );
    }, 0);

  const evChargingContract = basicData ? basicData.find((d) => contractIsEvCharging(d)) : undefined;

  const localStorageTag = window.localStorage.getItem('EvChargingTag') || '';
  const localStorageSite = parseInt(window.localStorage.getItem('EvChargingSite') || '');
  const localStorageChargerSocket = window.localStorage.getItem('EvChargingChargerSocket') || '';

  evChargerInfo?.sites.sort((a, b) => {
    const aAvailable = countAvailableSockets(a.chargers);
    const bAvailable = countAvailableSockets(b.chargers);
    return bAvailable - aAvailable; // Sort descending by available sockets
  });
  evChargerInfo?.sites.sort((a, b) => {
    const aIsLocal = a.groupId === localStorageSite;
    const bIsLocal = b.groupId === localStorageSite;
    if (aIsLocal && !bIsLocal) return -1;
    if (!aIsLocal && bIsLocal) return 1;
    return 0;
  });

  const totalAvailableSockets =
    evChargerInfo !== undefined && evChargerInfo.sites.length > 0 ? countAvailableSockets(evChargerInfo.sites.flatMap((site) => site.chargers)) : 0;
  let usedSockets = 0;

  const getStatus = (): void | EvChargingStatus => {
    async function fetchChargingStatus(): Promise<void | EvChargingStatus> {
      if (selectedMeteringPointContract && localStorageChargerSocket !== '') {
        const chargerId = localStorageChargerSocket.split('-')[0];
        const socketId = localStorageChargerSocket.split('-')[1];
        const chargeStatusRequest = await fetchEvChargingStatus(
          selectedMeteringPointContract.customerId,
          users.activeUser.userId,
          chargerId,
          socketId,
          localStorageTag,
          '0',
        );

        if (chargeStatusRequest.evseTransactionId) {
          dispatchEV(['changeChargerStatus', chargeStatusRequest]);
        }
      }
    }

    fetchChargingStatus();
  };

  React.useEffect(() => {
    if (config?.evCharging === true && evChargingContract !== undefined && (chargerStatus === undefined || !chargerStatus.evseTransactionId)) {
      getStatus();
    }
  }, [chargerStatus, selectedMeteringPointContract, localStorageChargerSocket, users.activeUser.userId]);

  return (
    <>
      {config?.evCharging === true && evChargingContract !== undefined && evChargerInfo !== undefined && evChargerInfo.sites.length > 0 && (
        <Liftup link={`/evCharging`} className="green">
          <h4>{t(`evCharging:landingPage.liftTitle`)}</h4>
          {chargerStatus?.status === 'CHARGING' ? (
            <div>
              <span className={styles.evSite}>
                {evChargerInfo.sites.find((site) => site.chargers.find((charger) => charger.chargerId === chargerStatus?.chargerId))?.groupName}
              </span>
              <h3 className="h2">
                <span className={styles.chargingIndicator}></span>
                {t(`evCharging:landingPage.charging`)}
              </h3>
            </div>
          ) : (
            evChargerInfo.sites.map((site, index) => {
              if (index > 1) return null; // Limit to first 2 sites
              usedSockets += countAvailableSockets(site.chargers);
              return (
                <div key={site.groupId}>
                  <span className={styles.evSite}>{site.groupName}</span>
                  <h3 className="h2">{`${countAvailableSockets(site.chargers)} ${t(`evCharging:landingPage.available`)}`}</h3>
                </div>
              );
            })
          )}
          {chargerStatus?.status !== 'CHARGING' && evChargerInfo.sites.length > 2 && (
            <p>
              + {evChargerInfo.sites.length - 2} {t(`evCharging:landingPage.otherSites`)}
              {totalAvailableSockets === usedSockets && t(`evCharging:landingPage.withoutAvailableChargers`)}
            </p>
          )}
        </Liftup>
      )}
    </>
  );
};

export default EVChargerLift;
